import React, { useMemo } from 'react';
import { ListingTable, type ListingColumn, type ListingGroup } from '@/shared/ui/components/basic/listing-table';
import { formatPropertyPrice } from '@/shared/utils/format-property-price';
import type { PropertyResponseType } from '@services/housien/types';
import {
  aggregate as aggregateGroup,
  ROWS_PER_GROUP,
  type GroupAggregate,
  type GroupBy,
  type PropertyGroup,
} from '../../pages/property-grouping';
import { GroupHeaderRow } from './GroupHeaderRow';
import { GroupShowMoreRow } from './GroupShowMoreRow';

interface GroupedListingTableProps {
  /** The active grouping dimension. */
  by: Exclude<GroupBy, 'none'>;
  /** The groups to render, already ordered by the caller. */
  groups: PropertyGroup[];
  /** The listing columns (shared with the flat table). */
  columns: ListingColumn<PropertyResponseType>[];
  /** Row click handler (navigates to the property). */
  onRowClick: (p: PropertyResponseType) => void;
  /** Narrows the whole list to the given group. */
  onFilterGroup: (group: PropertyGroup) => void;
  /** Per-group aggregate stats; null when still pending (partial load). */
  aggregates: Map<string, GroupAggregate | null>;
  /** Reports whether a group key is collapsed. */
  isCollapsed: (key: string) => boolean;
  /** Toggles a group's collapsed state. */
  onToggleCollapsed: (key: string) => void;
  /** Reports whether a group is showing all its rows. */
  isShowingAll: (key: string) => boolean;
  /** Toggles a group's "show all rows" state. */
  onToggleShowAll: (key: string) => void;
  /** Controlled sort key (only `price` orders rows within groups today). */
  sortKey?: string | null;
  /** Controlled sort direction. */
  sortDir?: 'asc' | 'desc' | null;
  /** Table min width before the container scrolls sideways. */
  minWidth?: string;
}

/**
 * Resolves a property's sale price (then rental) as the scalar the price
 * column orders on, matching the server's `sale_price` sort key.
 *
 * @param p - The property row.
 * @returns The comparable price, or null when unknown (sorts last).
 */
function priceScalar(p: PropertyResponseType): number | null {
  const { salePrice, rentalPrice } = formatPropertyPrice(p as unknown as Record<string, unknown>);
  return salePrice ?? rentalPrice ?? null;
}

/**
 * Compares two nullable numbers nulls-last for a signed direction.
 *
 * @param a - The first value (null sorts last).
 * @param b - The second value (null sorts last).
 * @param sign - +1 for ascending, -1 for descending.
 * @returns The comparator result.
 */
function compareNullableNumber(a: number | null, b: number | null, sign: number): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  return sign * (a - b);
}

/**
 * Orders a group's rows by the active price sort, nulls last. Any other sort
 * key (or none) leaves the incoming order untouched.
 *
 * @param rows - The group's rows.
 * @param sortKey - The active column key.
 * @param sortDir - The active direction.
 * @returns The ordered rows (a copy when sorted, the input otherwise).
 */
function orderRows(
  rows: PropertyResponseType[],
  sortKey: string | null | undefined,
  sortDir: 'asc' | 'desc' | null | undefined,
): PropertyResponseType[] {
  if (sortKey !== 'price' || !sortDir) return rows;
  const sign = sortDir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => compareNullableNumber(priceScalar(a), priceScalar(b), sign));
}

/**
 * The desktop grouped property table. It delegates to the shared, DS-registered
 * `ListingTable` via its backward-compatible `groups` capability, so the markup
 * (compact head, 64px rows, borders, hover) is literally the shared table — only
 * the per-group header cells ({@link GroupHeaderRow}) and the show-more footer
 * ({@link GroupShowMoreRow}) are feature-specific.
 *
 * @param props - The groups, columns, aggregates, and interaction handlers.
 * @returns The grouped table element.
 */
export const GroupedListingTable: React.FC<GroupedListingTableProps> = ({
  by,
  groups,
  columns,
  onRowClick,
  onFilterGroup,
  aggregates,
  isCollapsed,
  onToggleCollapsed,
  isShowingAll,
  onToggleShowAll,
  sortKey,
  sortDir,
  minWidth = '860px',
}) => {
  const listingGroups: ListingGroup<PropertyResponseType>[] = useMemo(
    () =>
      groups.map((group) => {
        const agg = aggregates.has(group.key) ? aggregates.get(group.key)! : aggregateGroup(group.items);
        const collapsed = isCollapsed(group.key);
        const showingAll = isShowingAll(group.key);
        const ordered = orderRows(group.items, sortKey, sortDir);
        const visibleRows = showingAll ? ordered : ordered.slice(0, ROWS_PER_GROUP);
        return {
          key: group.key,
          collapsed,
          rows: visibleRows,
          headerRowProps: {
            role: 'row',
            'aria-expanded': !collapsed,
            'data-testid': 'group-header-row',
            onClick: () => onToggleCollapsed(group.key),
          } as React.HTMLAttributes<HTMLTableRowElement>,
          header: (
            <GroupHeaderRow
              by={by}
              group={group}
              aggregate={agg}
              collapsed={collapsed}
              onToggle={() => onToggleCollapsed(group.key)}
              onFilterGroup={() => onFilterGroup(group)}
            />
          ),
        };
      }),
    [groups, aggregates, isCollapsed, isShowingAll, sortKey, sortDir, by, onToggleCollapsed, onFilterGroup],
  );

  return (
    <ListingTable
      data={[]}
      columns={columns}
      onRowClick={onRowClick}
      density="compact"
      minWidth={minWidth}
      bare
      testId="grouped-listing-table"
      rowTestId="property-row"
      groups={listingGroups}
      renderGroupFooter={(group) => {
        const source = groups.find((g) => g.key === group.key);
        const hiddenCount = (source?.items.length ?? 0) - ROWS_PER_GROUP;
        if (hiddenCount <= 0) return null;
        return (
          <GroupShowMoreRow
            colSpan={columns.length}
            groupLabel={source?.label ?? ''}
            hiddenCount={hiddenCount}
            showingAll={isShowingAll(group.key)}
            onToggle={() => onToggleShowAll(group.key)}
          />
        );
      }}
    />
  );
};
